import React, { useState, useRef } from "react";
import { Controller } from "react-hook-form";
import { InputTextarea } from "primereact/inputtextarea";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import classNames from "classnames";
import SpamSuggestions from "./spam-suggestions";
import { Text } from "../../../components/Text";
import { ReactComponent as Emoji } from "../../../assets/svgIcons/emoji.svg";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import "./stylles.css";
import { CustomButton } from "../../../components/CustomButton";

const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;
const TEMPLATE_DOC_URL = process.env.REACT_APP_TEMPLATE_DOC_URL;

const TemplateBodyField = ({
  control,
  textAreaRef,
  getFormErrorMessage = () => {},
  SPAM_SUGGESTIONS,
  showSpamSuggestions,
  setShowSpamSuggestions,
  checkForSpam,
  messageVariables,
  messageVarCount,
  setMessageVarCount,
  addVariablesClick,
  handleEmojiClick,
  tempVariables,
  setTempvariables,
  isVariableExist,
  templeteVariablesType,
  setValue,
  watchContent,
  styles,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const menuRef = useRef(null);

  return (
    <div className="w-full relative template-body-container">
      <div className="mb-2">
        <label htmlFor="content" className="inline-block">
          Body *
        </label>
      </div>

      <Controller
        name="content"
        control={control}
        spellCheck={true}
        rules={{ required: "Content is required." }}
        render={({ field, fieldState }) => {
          const spamMatches = checkForSpam(field.value || "");
          const contentLength = field.value?.length || 0;

          // Count emojis in the text
          const emojiCount = (
            field.value?.match(
              /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu
            ) || []
          ).length;

          const handleGenerateWithAI = async () => {
            const prompt = field.value;
            if (!prompt) {
              setApiError("Please enter a prompt to generate content.");
              return;
            }

            setIsLoading(true);
            setApiError(null);
            setShowTypingIndicator(true);

            try {
              const response = await fetch(N8N_WEBHOOK_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  chatInput: prompt,
                  sessionId: "template-body-session",
                }),
              });

              if (!response.ok)
                throw new Error(`HTTP error! ${response.status}`);

              const data = await response.json();
              setShowTypingIndicator(false);

              const aiResponse =
                (data && data[0]?.output) ||
                data?.output ||
                "Sorry, I couldn't process that.";
              field.onChange(aiResponse);
            } catch (error) {
              console.error("Error calling n8n webhook:", error);
              setApiError("Failed to generate content. Please try again.");
              setShowTypingIndicator(false);
            } finally {
              setIsLoading(false);
            }
          };

          const handleVariablesClick = (e) => {
            menuRef.current.toggle(e);
          };

          const handleGuideClick = () => {
            window.open(TEMPLATE_DOC_URL, "_blank");
          };

          const handleEmojiToggle = (event) => {
            event.stopPropagation();
            setShowEmoji((prev) => !prev);
          };

          return (
            <div className="relative">
              <div className="relative">
                <InputTextarea
                  className={classNames(
                    {
                      "p-invalid": fieldState.invalid || spamMatches.length > 0,
                    },
                    "w-full template-body-textarea"
                  )}
                  id={field.name}
                  value={field.value}
                  rows={5}
                  cols={30}
                  {...field}
                  maxLength={550}
                  placeholder="Please Enter A Short Description Before Generating With AI."
                  ref={textAreaRef}
                  disabled={isLoading}
                />

                {/* Character and emoji count in bottom right */}
                <div className="absolute bottom-2 right-2 char-counter-box">
                  <Text size="small" color="text-secondary">
                    {contentLength}/550 • {emojiCount}/10
                  </Text>
                </div>
              </div>

              {showTypingIndicator && (
                <div className="typing-indicator mt-2">
                  <Text size="small" color="text-secondary">
                    AI is typing
                    <span className="typing-dots"></span>
                  </Text>
                </div>
              )}

              {/* All 4 buttons on the same line */}
              <div className="button-container flex gap-2 mt-3">
                <CustomButton
                  label={isLoading ? "Generating..." : "Generate with AI"}
                  icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-robot"}
                  onClick={handleGenerateWithAI}
                  disabled={isLoading}
                  size="small"
                  className="generate-btn p-button-help"
                />
                <Button
                  label="Variables"
                  type="button"
                  icon="pi pi-code"
                  onClick={handleVariablesClick}
                  size="small"
                  outlined
                  className="p-button-secondary"
                />
                <Button
                  label="User Guide"
                  type="button"
                  icon="pi pi-book"
                  onClick={handleGuideClick}
                  size="small"
                  outlined
                  className="p-button-secondary"
                />
                <Button
                  onClick={handleEmojiToggle}
                  size="small"
                  type="button"
                  outlined
                  className="p-button-secondary p-button-icon-only"
                  aria-label="Insert emoji"
                >
                  <Emoji className="emoji-btn-icon" />
                </Button>
              </div>

              {/* Emoji Picker */}
              {showEmoji && (
                <div
                  className={`absolute ${styles.emoji}`}
                  style={{ right: "0", top: "100%", zIndex: 1000 }}
                >
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiClick}
                    onClickOutside={() => setShowEmoji(false)}
                  />
                </div>
              )}

              {/* Variables Menu */}
              <Menu model={messageVariables} popup ref={menuRef} />

              {apiError && (
                <Text className="api-error mt-2" size="small">
                  {apiError}
                </Text>
              )}

              {spamMatches.length > 0 && (
                <SpamSuggestions
                  spamMatches={spamMatches}
                  showSuggestions={showSpamSuggestions}
                  setShowSuggestions={setShowSpamSuggestions}
                  fieldValue={field.value}
                  SPAM_SUGGESTIONS={SPAM_SUGGESTIONS}
                  onReplace={(word) => {
                    const updated = field.value.replace(
                      new RegExp(word, "gi"),
                      SPAM_SUGGESTIONS[word] || ""
                    );
                    field.onChange(updated);
                  }}
                />
              )}
              {getFormErrorMessage("content")}
            </div>
          );
        }}
      />

      <Tooltip target=".open-doc-icon" />
    </div>
  );
};

export default TemplateBodyField;
